<template>
  <modal v-bind="$attrs" v-on="$listeners" :title="title" :errors="{}">
    <template v-slot:header></template>
    <template v-slot:body>
      <el-form class="uploader" name="fileList">
        <div class="upload-instructions">
          <p>{{$t('organizations.invite_users.import.instructions2')}}</p>
          <a
            href="/files/ELSA-Userlist.xlsx"
            download
          >{{$t('organizations.invite_users.import.get_template_xlsx')}}</a>
          <p>{{$t('common.or')}}</p>
          <a
            href="/files/ELSA-Userlist.csv"
            download
          >{{$t('organizations.invite_users.import.get_template_csv')}}</a>
          <p :style="{'margin-left': '0px'}">,</p>
          <p
            :style="{'margin-left': '0px'}"
          >{{$t('organizations.invite_users.import.instructions3')}}</p>
        </div>

        <slot
          v-if="hideUploader"
        >{{$t('organizations.invite_users.multiple_mail_to_invite', { numberOfInvites: numberOfEmails })}}</slot>

        <el-upload
          v-loading="loading"
          class="upload-files"
          :action="actionUrl"
          :before-upload="getActionUrl"
          :http-request="submitFile"
          :on-success="onSuccess"
          v-bind:class="{'hide-uploader': hideUploader, 'hide-file-list': hideFileList }"
          :drag="true"
          :file-list="form.fileList"
          :show-file-list="true"
          :auto-upload="true"
          :on-change="updateFileList"
          :limit="1"
          :on-remove="updateFileList"
        >
          <i class="elsa-icon-import-grey"></i>
          <div class="el-upload__text">
            {{$t('organizations.invite_users.import.instructions')}}
            <p>{{$t('common.or')}}</p>
            <chip icon>{{$t('organizations.invite_users.import.browse_files')}}</chip>
          </div>
        </el-upload>
        <el-form-item class="consent" prop="consent" show-message>
          <el-checkbox
            class="elsa-checkbox"
            label="consent"
            v-model.lazy="form.consent"
          >{{ $t('organizations.users_section.modals.invite_users.consent1', { organizationName: organizationName }) }}</el-checkbox>
        </el-form-item>
      </el-form>
    </template>
    <template v-slot:footer>
      <div class="actions">
        <primary-button
          :disabled="!canSubmit"
          @click.native="handleContinue"
        >{{ $t('common.continue') }}</primary-button>
        <tertiary-button @click.native="closeModal">{{ $t('common.cancel') }}</tertiary-button>
      </div>
    </template>
  </modal>
</template>

<script>
import Modal from "@/components/common/Modal";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import { STUDENT } from "../../../constants/userRoles";
import Chip from "@/components/common/Chip";
import { mapActions } from "vuex";
import { warningMessage } from "../../../utils/messages";
import {
  compose,
  pathOr,
  isEmpty,
  includes,
  length,
  defaultTo,
  path
} from "ramda";
import { readFile, uploadFile, allowedTypes } from "@/utils/uploader";
export default {
  components: {
    Modal,
    PrimaryButton,
    Chip,
    TertiaryButton
  },
  data() {
    return {
      actionUrl: "",
      uploadId: null,
      loading: false,
      errors: null,
      numberOfEmails: 0,
      form: {
        students: [],
        fileList: [],
        fileReady: false
      },
      canSubmit: false
    };
  },
  props: {
    title: {
      type: String
    },
    organizationType: {
      type: String,
      default: ""
    },
    organizationId: {},
    classId: {
      default: null
    },
  },
  computed: {
    hideUploader() {
      return this.form.fileList.length > 0;
    },
    hideFileList() {
      return this.form.fileList.length === 0;
    },
    organizationName() {
      const organizationName = compose(
        defaultTo(this.$store.getters["organization/currentOrganization"].name),
        path(["organizationData", "name"])
      )(this);

      return organizationName;
    }
  },
  methods: {
    closeModal() {
      this.$emit("closed");
    },
    handleContinue() {
      const option = { uploadId: this.uploadId };
      this.updateBuildingClass({ consent: this.form.consent });
      this.$emit("update:uploadId", this.uploadId);
      this.$emit("closed", option);
    },
    async getActionUrl(file) {
      this.loading = true;
      this.errors = false;
      const fileSize = file.size;
      const contentType = file.type;
      const organizationId = this.organizationId;
      const groupId = this.classId;
      const fileName = file.name;
      const role = STUDENT;
      const payload = {
        fileSize,
        contentType,
        organizationId,
        groupId,
        fileName,
        role
      };
      if (!includes(contentType, allowedTypes)) {
        this.errors = true;
        this.updateFileList(null, []);
        this.loading = false;
        warningMessage(this.$t("common.file_type_not_allowed"))(this);
        return Promise.reject(false);
      }

      try {
        const req = await this.getInviteUploadUrl({
          ...payload
        });

        this.actionUrl = req.data.presigned_url;
        this.uploadId = req.data.id;
      } catch (e) {
        // show warning
        // please try again
        this.errors = true;
        this.updateFileList(null, []);
        this.loading = false;
        warningMessage(this.$t("common.generic_error_message"))(this);
        return Promise.reject(false);
      }
    },
    async submitFile(request) {
      try {
        const { action: url, file } = request;
        const rawFile = await readFile(file);
        return await uploadFile(url, file.type, rawFile);
      } catch (e) {
        this.errors = true;
        this.loading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    // eslint-disable-next-line no-unused-vars
    async onSuccess(response, file, fileList) {
      try {
        const req = await this.getInviteUploadEmails({
          uploadId: this.uploadId
        });

        if (compose(isEmpty, pathOr([], ["data", "emails"]))(req)) {
          this.loading = false;
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }

        this.numberOfEmails = compose(
          length,
          pathOr([], ["data", "emails"])
        )(req);
        this.form.fileReady = true;
        this.loading = false;
      } catch (e) {
        // show warning
        this.errors = true;
        this.loading = false;
        return warningMessage(this.$t("common.generic_error_message"))(this);
      }
    },
    updateFileList(file, fileList) {
      this.form.fileList = fileList;
      if (!fileList.length) this.form.fileReady = false;
    },
    ...mapActions({
      updateBuildingClass: "classes/updateBuildingClass"
    }),
    ...mapActions("invite", [
      "getInviteUploadUrl",
      "getInviteUploadEmails",
      "updateInviteUploadEmails"
    ])
  },
  watch: {
    form: {
      immediate: true,
      deep: true,
      handler(newForm) {
        this.canSubmit = newForm.fileReady && !this.errors;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
